import { Alert, Layout } from 'antd';
import React, { useState } from 'react';

import {
    ALL_KRAJ_LABELS,
    BackToModesLink,
    Header,
    KRAJ_FULL_LABEL_BY_CODE,
    KRAJ_OPTIONS,
    OptionButton,
    OptionGrid,
    Page,
    Question,
    QuizFinalResult,
    QuizPanel,
    RegionShape as RegionShapeRenderer,
    RoundResultModal,
    RoundStatus,
    ShapeStage,
    makeOptions,
    shuffleArray,
    useKrajFeatures,
} from '../../components/quiz';

const { Content } = Layout;
const TOTAL_ROUNDS = KRAJ_OPTIONS.length;

const makeQuestion = region => {
    if (!region) return null;
    const label = KRAJ_FULL_LABEL_BY_CODE[region.code] || region.name;
    return {
        code: region.code,
        name: label,
        options: makeOptions(label, ALL_KRAJ_LABELS),
    };
};

const RegionShapeGame = () => {
    const { features, error } = useKrajFeatures();
    const [questionQueue, setQuestionQueue] = useState(() => shuffleArray(KRAJ_OPTIONS));
    const [round, setRound] = useState(1);
    const [score, setScore] = useState(0);
    const [question, setQuestion] = useState(() => makeQuestion(questionQueue[0]));
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [rounds, setRounds] = useState([]);
    const [finished, setFinished] = useState(false);

    const maxScore = TOTAL_ROUNDS * 100;
    const targetFeature = features?.find(feature => feature.id === question.code);
    const isCorrect = selectedAnswer === question.name;

    const restart = () => {
        const nextQueue = shuffleArray(KRAJ_OPTIONS);
        setQuestionQueue(nextQueue);
        setRound(1);
        setScore(0);
        setQuestion(makeQuestion(nextQueue[0]));
        setSelectedAnswer(null);
        setModalVisible(false);
        setRounds([]);
        setFinished(false);
    };

    const answer = option => {
        if (selectedAnswer) return;
        setSelectedAnswer(option);
        const correct = option === question.name;
        if (correct) setScore(s => s + 100);
        setRounds(prev => [...prev, { correct, score: correct ? 100 : 0, label: question.name }]);
        setModalVisible(true);
    };

    const advanceRound = () => {
        const nextRound = round + 1;
        setModalVisible(false);
        setRound(nextRound);
        setQuestion(makeQuestion(questionQueue[nextRound - 1]));
        setSelectedAnswer(null);
    };

    const showFinal = () => {
        setModalVisible(false);
        setFinished(true);
    };

    if (finished) {
        return <QuizFinalResult score={score} totalRounds={TOTAL_ROUNDS} rounds={rounds} onRestart={restart} />;
    }

    return (
        <>
            <QuizPanel>
                <RoundStatus round={round} score={score} maxScore={maxScore} totalRounds={TOTAL_ROUNDS} />
                <Question>
                    <p>Poznej kraj podle tvaru</p>
                    <h2>Jaký kraj má tento obrys?</h2>
                    <p>Vyber správnou odpověď ze čtyř možností.</p>
                </Question>
                {error && <Alert type="error" message="Mapa se nepodařila načíst" description={error.message} />}
                <ShapeStage>
                    {targetFeature ? (
                        <RegionShapeRenderer feature={targetFeature} />
                    ) : (
                        <Alert type="info" message="Načítám tvar kraje..." />
                    )}
                </ShapeStage>
                <OptionGrid>
                    {question.options.map(option => (
                        <OptionButton
                            key={option}
                            answer={option}
                            correctAnswer={question.name}
                            disabled={!!selectedAnswer}
                            selectedAnswer={selectedAnswer}
                            onClick={() => answer(option)}
                        />
                    ))}
                </OptionGrid>
            </QuizPanel>
            <RoundResultModal
                visible={modalVisible}
                correct={isCorrect}
                round={round}
                totalRounds={TOTAL_ROUNDS}
                onNext={advanceRound}
                onShowResult={showFinal}
                wrongVerdict={selectedAnswer ? `${selectedAnswer} není správná odpověď :(` : null}
            >
                <h3>Správná odpověď</h3>
                <p>
                    <strong style={{ fontSize: '1.2em' }}>{question.name}</strong>
                </p>
                {selectedAnswer && !isCorrect && (
                    <p>
                        Tvůj tip byl: <strong>{selectedAnswer}</strong>.
                    </p>
                )}
            </RoundResultModal>
        </>
    );
};

export const RegionShape = () => (
    <Content>
        <Page>
            <Header>
                <BackToModesLink />
                <h1>Tvar kraje</h1>
                <p>
                    Poznej český kraj podle obrysu. V každé hře projdeš všech {TOTAL_ROUNDS} krajů v náhodném pořadí a
                    vybíráš ze čtyř možností. Skvělý trénink slepé mapy. Data: hranice krajů ČSÚ (kraje-only.geo.json).
                </p>
            </Header>
            <RegionShapeGame />
        </Page>
    </Content>
);
